// Telemetry must never break the app. `window.Raven` is a CDN-loaded raven-js
// (see public/index.html) that is only configured when a Sentry DSN is present,
// and whose captureMessage can throw on some payloads/internal states — so every
// call is guarded and reporting failures are swallowed rather than surfaced.
export const errorHandler = {
  // Runs at boot, before React mounts — so a throw here can't be caught by any
  // ErrorBoundary and would blank #root outright (e.g. the CDN reporter blocked
  // by an ad blocker). Guarded like the rest; keep all Raven access in here.
  init: function (dsn: string) {
    try {
      window.Raven?.config(dsn).install()
    } catch {
      // reporting failure — see capture
    }
  },

  capture: function (errorMsg: unknown, context?: unknown) {
    if (process.env.NODE_ENV === 'production') {
      try {
        window.Raven?.captureMessage(errorMsg, { extra: context })
      } catch {
        // reporting failure — nothing safe to do but drop it
      }
      /* eslint-disable no-console */
    } else if (window.console && console.error) {
      console.error(errorMsg, context)
      /* eslint-enable no-console */
    }

    return null
  },

  // Prefer this for real Error objects: raven's captureException unwinds the
  // stack trace (captureMessage does not), which is what we want for the
  // component crashes the root ErrorBoundary catches.
  captureException: function (error: unknown, context?: unknown) {
    if (process.env.NODE_ENV === 'production') {
      try {
        window.Raven?.captureException(error, { extra: context })
      } catch {
        // reporting failure — see capture
      }
      /* eslint-disable no-console */
    } else if (window.console && console.error) {
      console.error(error, context)
      /* eslint-enable no-console */
    }

    return null
  },

  setUserContext: function (userId: unknown) {
    try {
      window.Raven?.setUserContext({ id: userId })
    } catch {
      // reporting failure — see capture
    }
  },

  deleteUserContext: function () {
    try {
      window.Raven?.setUserContext()
    } catch {
      // reporting failure — see capture
    }
  },
}
